/**
 * Discover-from-URL modal for semi-manual taxonomy discovery.
 *
 * Fetches a court / state-bar URL, harvests candidate labels, and proposes
 * pending case_subtype_mappings. Dry-run by default so the admin can review
 * what would be written before committing.
 */
import React, { useState } from 'react';
import {
  XMarkIcon,
  GlobeAltIcon,
  PlayIcon,
  CheckIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import { discoverFromUrl, type UrlDiscoveryResult } from './caseSubtypeTaxonomyApi';

interface Props {
  onClose: () => void;
  onCompleted: () => void;
  defaultStateCode?: string;
  defaultPracticeArea?: string;
  firmStates: string[];
  firmPracticeAreas: string[];
}

const DiscoverFromUrlModal: React.FC<Props> = ({
  onClose,
  onCompleted,
  defaultStateCode = '',
  defaultPracticeArea = '',
  firmStates,
  firmPracticeAreas,
}) => {
  const [url, setUrl] = useState('');
  const [stateCode, setStateCode] = useState(defaultStateCode);
  const [jurisdictionCode, setJurisdictionCode] = useState('');
  const [practiceArea, setPracticeArea] = useState(defaultPracticeArea);
  const [dryRun, setDryRun] = useState(true);
  const [allowCrossStateAlias, setAllowCrossStateAlias] = useState(false);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<UrlDiscoveryResult | null>(null);

  const handleRun = async () => {
    setError(null);
    if (!url.trim()) {
      setError('URL is required.');
      return;
    }
    if (!stateCode && !jurisdictionCode) {
      setError('Either state code or jurisdiction code is required.');
      return;
    }
    setRunning(true);
    try {
      const { discovery } = await discoverFromUrl({
        url: url.trim(),
        state_code: stateCode.trim().toUpperCase() || null,
        jurisdiction_code: jurisdictionCode.trim().toUpperCase() || null,
        practice_area: practiceArea || null,
        dry_run: dryRun,
        allow_cross_state_alias: allowCrossStateAlias,
      });
      setResult(discovery);
      if (!dryRun) onCompleted();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Discovery failed');
    } finally {
      setRunning(false);
    }
  };

  const plannedInserts = result
    ? result.producer_result.projected_mappings.filter((m) => m.action === 'insert').length
    : 0;
  const plannedUpdates = result
    ? result.producer_result.projected_mappings.filter((m) => m.action === 'update').length
    : 0;
  const plannedSkipped = result
    ? result.producer_result.projected_mappings.filter((m) => m.action === 'skip_existing_covered').length
    : 0;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/60 backdrop-blur-sm p-4">
      <div className="relative w-full max-w-3xl rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-xl overflow-hidden">
        <div className="h-1 bg-gradient-to-r from-violet-500 via-indigo-500 to-sky-500" />
        <div className="flex items-start justify-between px-5 pt-4 pb-3 border-b border-gray-100 dark:border-gray-700/50">
          <div className="flex items-start gap-2.5">
            <div className="p-2 rounded-lg bg-violet-50 dark:bg-violet-900/30">
              <GlobeAltIcon className="h-5 w-5 text-violet-600 dark:text-violet-400" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                Discover Case Subtypes from URL
              </h2>
              <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
                Paste a court self-help index, state bar page, or practice-area catalog URL.
                We harvest candidate labels and propose pending mappings — no validated writes.
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="rounded-md p-1 text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
            title="Close"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        <div className="px-5 py-4 space-y-3 max-h-[70vh] overflow-y-auto">
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-1">
              URL
            </label>
            <input
              type="url"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://selfhelp.courts.ca.gov/family-law"
              className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm text-gray-700 dark:text-gray-200 focus:outline-none focus:ring-1 focus:ring-violet-500"
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-1">
                State
              </label>
              {firmStates.length > 0 ? (
                <select
                  value={stateCode}
                  onChange={(e) => setStateCode(e.target.value)}
                  className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-2 py-2 text-sm text-gray-700 dark:text-gray-200 focus:outline-none focus:ring-1 focus:ring-violet-500"
                >
                  <option value="">—</option>
                  {firmStates.map((s) => (<option key={s} value={s}>{s}</option>))}
                </select>
              ) : (
                <input
                  type="text"
                  value={stateCode}
                  onChange={(e) => setStateCode(e.target.value.toUpperCase().slice(0, 2))}
                  placeholder="CA"
                  className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-2 py-2 text-sm text-gray-700 dark:text-gray-200 focus:outline-none focus:ring-1 focus:ring-violet-500"
                />
              )}
            </div>
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-1">
                Jurisdiction (optional)
              </label>
              <input
                type="text"
                value={jurisdictionCode}
                onChange={(e) => setJurisdictionCode(e.target.value.toUpperCase())}
                placeholder="CA-06073"
                className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-2 py-2 text-sm text-gray-700 dark:text-gray-200 focus:outline-none focus:ring-1 focus:ring-violet-500"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-1">
                Practice Area hint
              </label>
              {firmPracticeAreas.length > 0 ? (
                <select
                  value={practiceArea}
                  onChange={(e) => setPracticeArea(e.target.value)}
                  className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-2 py-2 text-sm text-gray-700 dark:text-gray-200 focus:outline-none focus:ring-1 focus:ring-violet-500"
                >
                  <option value="">auto-classify</option>
                  {firmPracticeAreas.map((p) => (<option key={p} value={p}>{p.replace(/_/g, ' ')}</option>))}
                </select>
              ) : (
                <input
                  type="text"
                  value={practiceArea}
                  onChange={(e) => setPracticeArea(e.target.value)}
                  placeholder="family_law"
                  className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-2 py-2 text-sm text-gray-700 dark:text-gray-200 focus:outline-none focus:ring-1 focus:ring-violet-500"
                />
              )}
            </div>
          </div>

          <div className="space-y-1.5">
            <label className="inline-flex items-center gap-2 text-sm text-gray-700 dark:text-gray-200">
              <input
                type="checkbox"
                checked={dryRun}
                onChange={(e) => setDryRun(e.target.checked)}
                className="rounded text-violet-600 focus:ring-violet-500"
              />
              Dry run (preview without writing)
            </label>
            <label className="flex items-start gap-2 text-sm text-gray-700 dark:text-gray-200">
              <input
                type="checkbox"
                checked={allowCrossStateAlias}
                onChange={(e) => setAllowCrossStateAlias(e.target.checked)}
                className="mt-0.5 rounded text-violet-600 focus:ring-violet-500"
              />
              <span>
                Allow cross-state alias mapping
                <span className="block text-xs text-gray-500 dark:text-gray-400">
                  Leave off to preserve the jurisdiction's letter-for-letter term (e.g. "divorce" stays "divorce" in GA).
                  Enable only if you have verified the alias is correct for this state.
                </span>
              </span>
            </label>
          </div>

          {error && (
            <div className="flex items-start gap-2 bg-rose-50 dark:bg-rose-900/20 border border-rose-200 dark:border-rose-800 text-rose-700 dark:text-rose-300 rounded-lg p-2.5 text-sm">
              <ExclamationTriangleIcon className="h-4 w-4 mt-0.5 shrink-0" />
              {error}
            </div>
          )}

          {result && (
            <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-900/30 p-3 space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-1.5 text-sm font-medium text-gray-700 dark:text-gray-200">
                  <CheckIcon className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
                  {result.dry_run ? 'Dry-run preview' : 'Persisted'}
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400">
                  {result.filtered_label_count} candidate labels from {result.raw_label_count} raw
                </div>
              </div>
              <div className="grid grid-cols-3 gap-2 text-sm">
                <StatCell label="Inserts" value={plannedInserts} tone="emerald" />
                <StatCell label="Updates" value={plannedUpdates} tone="indigo" />
                <StatCell label="Skipped" value={plannedSkipped} tone="gray" />
              </div>
              {result.producer_result.projected_mappings.length > 0 && (
                <div className="max-h-48 overflow-y-auto rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
                  <table className="min-w-full text-xs">
                    <thead className="bg-gray-50 dark:bg-gray-900/40 text-gray-500 dark:text-gray-400">
                      <tr>
                        <th className="px-2 py-1.5 text-left">Action</th>
                        <th className="px-2 py-1.5 text-left">Practice Area</th>
                        <th className="px-2 py-1.5 text-left">Local → Canonical</th>
                        <th className="px-2 py-1.5 text-left">Reason</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 dark:divide-gray-700/50">
                      {result.producer_result.projected_mappings.slice(0, 100).map((m, i) => (
                        <tr key={i}>
                          <td className="px-2 py-1.5 font-mono">{m.action}</td>
                          <td className="px-2 py-1.5">{m.practice_area}</td>
                          <td className="px-2 py-1.5 font-mono">{m.local_key} → {m.canonical_key}</td>
                          <td className="px-2 py-1.5 text-gray-500 dark:text-gray-400">{m.reason}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {result.discarded.length > 0 && (
                <div className="text-xs text-gray-500 dark:text-gray-400">
                  {result.discarded.length} labels discarded (e.g. noisy labels, unknown practice area).
                </div>
              )}
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-gray-100 dark:border-gray-700/50">
          <button
            onClick={onClose}
            className="rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-1.5 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-600"
          >
            Close
          </button>
          <button
            onClick={handleRun}
            disabled={running}
            className="inline-flex items-center gap-1.5 rounded-md bg-violet-600 hover:bg-violet-500 disabled:opacity-50 text-white px-3 py-1.5 text-sm font-medium"
          >
            {running ? (
              <span className="inline-block h-4 w-4 rounded-full border-2 border-white border-t-transparent animate-spin" />
            ) : (
              <PlayIcon className="h-4 w-4" />
            )}
            {dryRun ? 'Run dry-run' : 'Run & persist pending'}
          </button>
        </div>
      </div>
    </div>
  );
};

const TONE_CLASSES: Record<string, string> = {
  emerald: 'text-emerald-700 dark:text-emerald-300',
  indigo: 'text-indigo-700 dark:text-indigo-300',
  gray: 'text-gray-600 dark:text-gray-300',
};

const StatCell: React.FC<{ label: string; value: number; tone: string }> = ({ label, value, tone }) => (
  <div className="rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-2">
    <div className="text-[10px] uppercase tracking-wider text-gray-500 dark:text-gray-400">{label}</div>
    <div className={`text-lg font-semibold ${TONE_CLASSES[tone] || TONE_CLASSES.gray}`}>{value}</div>
  </div>
);

export default DiscoverFromUrlModal;
